import { Fragment } from 'react';

import { cn } from '@nesso/ui/lib/utils';
import {
    Breadcrumb,
    BreadcrumbEllipsis,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbPage,
    BreadcrumbSeparator,
} from '@nesso/ui/primitives/breadcrumb';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@nesso/ui/primitives/dropdown-menu';

import { type SheetProps, useSheetStore } from '@/shared/stores/sheet-store';
import { TruncatedTextWithTooltip } from '@/shared/ui/text/truncated-text-with-tooltip';

type Props = {
    // The store snapshots `label` when a sheet opens; this lets the caller substitute fresher text.
    getLabel?: (sheet: SheetProps, index: number) => string | undefined;
    onNavigate?: (navigate: () => void) => void;
    // A page-level crumb for UI that sits above the top sheet without being one in the store.
    current?: string;
    // Starts the trail at the nearest open sheet of this type, dropping what sits beneath it. When no
    // such sheet is open the full trail renders.
    startAt?: SheetProps['type'];
    className?: string;
};

// Past this depth the middle collapses behind `…` so crumbs don't fight for a 360px sheet.
const COLLAPSE_AT_DEPTH = 4;

export function SheetBreadcrumb({ getLabel, onNavigate, current, startAt, className }: Props) {
    const openSheets = useSheetStore((state) => state.openSheets);
    const goBack = useSheetStore((state) => state.goBack);

    const sheetDepth = openSheets.length;
    const depth = sheetDepth + (current === undefined ? 0 : 1);

    const firstIndex = Math.max(
        0,
        startAt === undefined ? 0 : openSheets.findLastIndex((sheet) => sheet.type === startAt),
    );
    const visibleDepth = depth - firstIndex;

    // A single crumb is not a trail — the client chart keeps its own action-bar header.
    if (visibleDepth < 2) return null;

    const lastIndex = depth - 1;
    const isCollapsed = visibleDepth >= COLLAPSE_AT_DEPTH;

    const labelOf = (index: number) =>
        index >= sheetDepth
            ? (current ?? '')
            : (getLabel?.(openSheets[index], index) ?? openSheets[index].label);

    const navigateTo = (index: number) => () => goBack(sheetDepth - 1 - index);

    const handleCrumbClick = (index: number) => {
        const navigate = navigateTo(index);
        if (onNavigate) {
            onNavigate(navigate);
            return;
        }
        navigate();
    };

    const indexesFrom = (from: number, to: number) =>
        Array.from({ length: to - from }, (_, i) => from + i);

    const linkIndexes = isCollapsed
        ? [firstIndex, lastIndex - 1]
        : indexesFrom(firstIndex, lastIndex);
    const hiddenIndexes = isCollapsed ? indexesFrom(firstIndex + 1, lastIndex - 1) : [];

    function renderLink(index: number) {
        return (
            <BreadcrumbItem key={index} className="min-w-0">
                <BreadcrumbLink asChild className="truncate">
                    <button type="button" onClick={() => handleCrumbClick(index)}>
                        <TruncatedTextWithTooltip text={labelOf(index)} />
                    </button>
                </BreadcrumbLink>
            </BreadcrumbItem>
        );
    }

    return (
        <Breadcrumb className={cn('min-w-0', className)}>
            <BreadcrumbList className="flex-nowrap">
                {renderLink(linkIndexes[0])}
                <BreadcrumbSeparator className="shrink-0" />

                {isCollapsed && (
                    <>
                        <BreadcrumbItem className="shrink-0">
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <BreadcrumbEllipsis />
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="start">
                                    {hiddenIndexes.map((index) => (
                                        <DropdownMenuItem
                                            key={index}
                                            onClick={() => handleCrumbClick(index)}
                                        >
                                            {labelOf(index)}
                                        </DropdownMenuItem>
                                    ))}
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </BreadcrumbItem>
                        <BreadcrumbSeparator className="shrink-0" />
                    </>
                )}

                {linkIndexes.slice(1).map((index) => (
                    <Fragment key={index}>
                        {renderLink(index)}
                        <BreadcrumbSeparator className="shrink-0" />
                    </Fragment>
                ))}

                <BreadcrumbItem className="shrink-0">
                    <BreadcrumbPage className="text-foreground">
                        {labelOf(lastIndex)}
                    </BreadcrumbPage>
                </BreadcrumbItem>
            </BreadcrumbList>
        </Breadcrumb>
    );
}
